import React, {useContext, useEffect, useState} from 'react';
import {WMSContext} from "../context/WMSContext";
import {useHistory, useLocation} from "react-router-dom";
import {getTasks} from "../../../api/wms/tasks/list";
import {Authorizations} from "../../../utils/authorizations";
import {WMSRoutes} from "../../../utils/WMSRoutes";
import {WmsTaskStatus} from "../../../utils/WMSTaskStatus";
import {MissionListContext} from "./MissionListContext";
import MissionListDesktop from "./component/MissionListDesktop";
import pusherClient from "../../../utils/pusherClient";
import DeleteMissionModal from "./component/DeleteMissionModal";
import {deleteMission} from "../../../api/wms/tasks/delete";
import MissionListMobile from "./component/MissionListMobile";
import {WmsItemsStatus} from "../../../utils/WmsItemsStatus";

function MissionList() {
    const {currentInventory, currentOrganization, currentMemberAuthorizations, isMobile} = useContext(WMSContext)

    const [missionList, setMissionList] = useState([])
    const [selectedMission, setSelectedMission] = useState()
    const [openDeleteMissionModal, setOpenDeleteMissionModal] = useState(false)
    const [openValidModificationToast, setOpenValidModificationToast] = useState(false)
    const [loading, setLoading] = useState(true)

    const history = useHistory()

    const location = useLocation()

    const init = () => {
        getTasks(currentInventory, null, [WmsTaskStatus.IDLE, WmsTaskStatus.IN_PROGRESS, WmsTaskStatus.CANCELLED, WmsTaskStatus.COMPLETED]).then((response) => {
            response.forEach((task) => {
                task.totalItems = task.wmsItems.length
                task.inTrolleyItems = task.wmsItems.filter((item) => WmsItemsStatus.IN_TROLLEY === item.status).length
                task.canceledItems = task.wmsItems.filter((item) => WmsItemsStatus.CANCELED === item.status).length
                task.notFoundItems = 0 // Not found items are systematically removed from task. This is only for pusher messages.
            })
            setMissionList(response)
        }).finally(() => {
            setLoading(false)
        })
    }

    const handleOpenDeleteMissionModal = (mission) => {
        setSelectedMission(mission)
        setOpenDeleteMissionModal(true)
    }

    const handleDeleteMission = () => {
        deleteMission(selectedMission.id).then(() => {
            setOpenDeleteMissionModal(false)
            init()
        })
    }

    function handleTaskUpdate(data) {
        setMissionList(prev => {
            const task = prev.find((mission) => mission.id === data.id)
            if (!task || (task.status === data.status && task.member.user.fullName === data.member)) {
                return prev
            }
            return prev.map(mission => {
                if (mission.id !== data.id) {
                    return mission
                }

                return {
                    ...mission,
                    status: data.status,
                    metadata: {
                        partiallyComplete: data.partiallyComplete,
                        pastAssociatedMemberFullName: data.pastAssociatedMemberFullName,
                        pastAssociatedMemberId: data.pastAssociatedMemberId,
                    },
                    member: {
                        ...mission.member,
                        user: {
                            ...mission.member?.user,
                            fullName: data.member
                        }
                    }
                }
            })
        })
    }

    function handleTaskCreation(data) {
        setMissionList(prev => {
            if (prev.find((mission) => mission.id === data.id)) {
                return prev
            }

            const newTask = {
                id: data.id,
                name: data.name,
                status: data.status,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt,
                totalItems: data.totalItems,
                inTrolleyItems: data.inTrolleyItems,
                canceledItems: data.canceledItems,
                notFoundItems: data.notFoundItems,
                member: {
                    user: {
                        fullName: data.member
                    }
                }
            }
            return [newTask, ...prev]
        })
    }

    const handleWmsItemStatusUpdate = (data) => {
        setMissionList(prev => {
            if (!prev.some((mission) => data.updatedWmsItem.currentTask === mission.id || data.updatedWmsItem.oldTask === mission.id)) {
                return prev
            }

            return prev.map(mission => {
                if (mission.id !== data.updatedWmsItem.currentTask && mission.id !== data.updatedWmsItem.oldTask) {
                    return mission
                }

                const updatedMission = { ...mission }

                if (data.wmsItemOldStatus === WmsItemsStatus.IN_TROLLEY) {
                    updatedMission.inTrolleyItems = mission.inTrolleyItems - 1
                } else if (data.wmsItemOldStatus === WmsItemsStatus.CANCELED) {
                    updatedMission.canceledItems = mission.canceledItems - 1
                } else if (data.wmsItemOldStatus === WmsItemsStatus.NOT_FOUND) {
                    updatedMission.notFoundItems = mission.notFoundItems - 1
                }

                if (data.wmsItemNewStatus === WmsItemsStatus.IN_TROLLEY) {
                    updatedMission.inTrolleyItems = updatedMission.inTrolleyItems + 1
                } else if (data.wmsItemNewStatus === WmsItemsStatus.CANCELED) {
                    updatedMission.canceledItems = updatedMission.canceledItems + 1
                    updatedMission.totalItems = mission.totalItems - 1
                } else if (data.wmsItemNewStatus === WmsItemsStatus.NOT_FOUND) {
                    updatedMission.notFoundItems = mission.notFoundItems + 1
                    updatedMission.totalItems = mission.totalItems - 1
                }

                return updatedMission
            })
        })
    }

    useEffect(() => {
        if (currentInventory && currentOrganization) {
            const taskChannelName = `wmsTask${currentOrganization.id}`
            const wmsItemChannelName = `wms_item_${currentInventory.id}${currentOrganization.id}`

            const taskUpdateChannel = pusherClient.subscribe(taskChannelName)
            taskUpdateChannel.bind('update', handleTaskUpdate)
            taskUpdateChannel.bind('create', handleTaskCreation)

            const wmsItemUpdateChannel = pusherClient.subscribe(wmsItemChannelName)
            wmsItemUpdateChannel.bind('status_changed', handleWmsItemStatusUpdate)

            return () => {
                taskUpdateChannel.unbind('update', handleTaskUpdate)
                taskUpdateChannel.unbind('create', handleTaskCreation)
                pusherClient.unsubscribe('private-' + taskChannelName)

                wmsItemUpdateChannel.unbind('status_changed')
                pusherClient.unsubscribe('private-' + wmsItemChannelName)
            }
        }
    }, [currentInventory, currentOrganization]);

    useEffect(() => {
        if (currentMemberAuthorizations && currentInventory) {
            if (!currentMemberAuthorizations.some(authorization => Authorizations.WMS_MANAGER === authorization)) {
                history.push(WMSRoutes.DASHBOARD)
                return;
            }

            init()
        }
    }, [currentInventory, currentMemberAuthorizations]);

    return (
        <MissionListContext.Provider value={{
            missionList,
            selectedMission,
            openDeleteMissionModal,
            openValidModificationToast,
            loading,
            history,
            location,
            setSelectedMission,
            setOpenDeleteMissionModal,
            setOpenValidModificationToast,
            setLoading,
            handleOpenDeleteMissionModal,
        }}>
            {isMobile && (
                <MissionListMobile />
            )}

            {!isMobile && (
                <MissionListDesktop />
            )}

            {openDeleteMissionModal && (
                <DeleteMissionModal
                    open={openDeleteMissionModal}
                    handleClose={() => setOpenDeleteMissionModal(false)}
                    loading={loading}
                    handleValid={() => {
                        setLoading(true)
                        handleDeleteMission()
                    }}
                />
            )}

        </MissionListContext.Provider>
    );
}

export default MissionList;